import React, {
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

type ColorMode = "light" | "dark";

interface ColorModeContextType {
  mode: ColorMode;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextType>({
  mode: "light",
  toggleColorMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

interface AppThemeProviderProps {
  children: ReactNode;
}

// Dark mode card styles - no glass effects
const cardStyles = {
  light: {
    background: "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
  },
  dark: {
    background: "#1e1e1e",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.4)",
  },
};

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({
  children,
}) => {
  const [mode, setMode] = useState<ColorMode>(() => {
    // Check localStorage first
    const savedMode = localStorage.getItem("themeMode");
    if (savedMode === "light" || savedMode === "dark") {
      return savedMode;
    }
    // Check system preference
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
    return "light";
  });

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
    // Set data-theme attribute for CSS targeting
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "light"
            ? {
                primary: {
                  main: "#2bb39d",
                  light: "#5dc4b2",
                  dark: "#1f9c86",
                },
                secondary: {
                  main: "#036C5F",
                  light: "#348f82",
                  dark: "#024d43",
                },
                background: {
                  default: "#f0f2f5",
                  paper: "#ffffff",
                },
              }
            : {
                primary: {
                  main: "#5dc4b2",
                  light: "#8fd6c9",
                  dark: "#2bb39d",
                },
                secondary: {
                  main: "#348f82",
                  light: "#5fb0a2",
                  dark: "#036C5F",
                },
                background: {
                  default: "#121212",
                  paper: "#1e1e1e",
                },
              }),
        },
        typography: {
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          button: {
            textTransform: "none",
          },
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                transition: "background-color 0.3s ease, color 0.3s ease",
                ...(mode === "dark" && {
                  backgroundColor: "#121212",
                }),
                ...(mode === "light" && {
                  backgroundColor: "#f5f5f5",
                }),
              },
              "::-webkit-scrollbar": {
                width: "10px",
                height: "10px",
              },
              "::-webkit-scrollbar-track": {
                background:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                borderRadius: "10px",
              },
              "::-webkit-scrollbar-thumb": {
                background:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.2)",
                borderRadius: "10px",
                "&:hover": {
                  background:
                    mode === "dark"
                      ? "rgba(255, 255, 255, 0.3)"
                      : "rgba(0, 0, 0, 0.3)",
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                ...(mode === "dark" ? cardStyles.dark : cardStyles.light),
              },
              elevation1: {
                ...(mode === "dark" ? cardStyles.dark : cardStyles.light),
              },
              elevation2: {
                ...(mode === "dark" ? cardStyles.dark : cardStyles.light),
              },
              elevation3: {
                ...(mode === "dark" ? cardStyles.dark : cardStyles.light),
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                transition: "background-color 0.3s ease, box-shadow 0.3s ease",
                ...(mode === "dark" ? cardStyles.dark : cardStyles.light),
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                padding: "8px 16px",
                fontWeight: 600,
                transition: "all 0.3s ease",
                textTransform: "none",
              },
              contained: {
                boxShadow: "none",
                ...(mode === "dark" && {
                  backgroundColor: "#5dc4b2",
                  color: "#121212",
                }),
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    mode === "dark"
                      ? "0 8px 20px rgba(93, 196, 178, 0.3)"
                      : "0 8px 20px rgba(43, 179, 157, 0.3)",
                },
              },
              outlined: {
                borderWidth: "1.5px",
                ...(mode === "dark" && {
                  borderColor: "rgba(93, 196, 178, 0.5)",
                  color: "#5dc4b2",
                  "&:hover": {
                    borderColor: "#5dc4b2",
                    backgroundColor: "rgba(93, 196, 178, 0.1)",
                  },
                }),
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                "& .MuiOutlinedInput-root": {
                  transition: "all 0.3s ease",
                  ...(mode === "dark" && {
                    backgroundColor: "#2a2a2a",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#333333",
                      "& fieldset": {
                        borderColor: "#5dc4b2",
                      },
                    },
                  }),
                  ...(mode === "light" && {
                    background: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.95)",
                    },
                    "&.Mui-focused": {
                      background: "#ffffff",
                    },
                  }),
                },
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                ...(mode === "dark" ? cardStyles.dark : cardStyles.light),
                borderRadius: 12,
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderBottom:
                  mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.05)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              root: {
                borderColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.08)",
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
                ...(mode === "dark"
                  ? {
                      ...cardStyles.dark,
                      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    }
                  : {
                      ...cardStyles.light,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }),
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                ...(mode === "dark" ? cardStyles.dark : cardStyles.light),
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: {
                ...(mode === "dark" && {
                  backgroundColor: "#2a2a2a",
                }),
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
